import { isDefined } from '@sgme/fp';
import type { LiveTradeAmendRequest } from 'apiConnector/secondary-market/live/models';
import { createSelector } from 'reselect';
import { liveFormInputSelectors } from 'state/secondaryMarket/live/form/input/inputForm.selectors';
import { liveOriginalFormSelectors } from 'state/secondaryMarket/live/form/original/liveOriginalForm.selector';
import { computeSalesAdjustedPrice } from 'state/secondaryMarket/live/live.utils';
import { liveReferentialSelectors } from 'state/secondaryMarket/live/referential/liveReferential.selector';
import { liveTradeSelectors } from 'state/secondaryMarket/live/trade/trade.selector';
import type { SecondaryQuotationType } from 'state/secondaryMarket/productSearch';
import type { TrackedValue } from 'state/secondaryMarket/realtime/realtime.model';
import { secondaryMarketSelectorsFactory } from 'state/secondaryMarket/shared/selector';
import { computeCashAmount } from 'state/secondaryMarket/shared/utils';
import type { AppState } from 'state/state.model';
import * as fromSecondaryPostEventsWorkspace from 'state/workspaces/secondary-market/post-event/secondaryMarketPostEvent.selector';
import { getProductKey } from 'state/workspaces/secondary-market/quotes/secondaryMarketQuotes.selector';
import { getTabIsFrozen, getWorkspaceType } from 'state/workspaces/tabs/tabs.selector';

import { getRelevantPriceDependingOnTradeWay } from '../cpOrder/cpOrder.utils';
import type { StreamingPriceForDisplay } from '../realtime/realtime.selector';
import {
  getRealtimeAskSettlementPrice,
  getRealtimeBidSettlementPrice,
  getRealtimeLastUpdate,
  getRealtimeStatus,
  getRealtimeUnderlyingPrice,
  getRealtimeUnderlyingPriceUnit,
  getStreamingPriceForDisplayByTradeway,
} from '../realtime/realtime.selector';
import type { TradeWay } from '../secondaryMarket.model';
import type { LiveForm } from './form/input/inputForm.model';
import type { LiveStatus } from './live.model';
import {
  getLiveQuoteErrorMessage,
  getLiveQuotePrice,
  getLiveQuotePriceType,
  getLiveQuoteQuantity,
  getLiveQuoteQuotationType,
  getLiveQuoteStatus,
  getLiveQuoteTimeout,
  getLiveQuoteTradeWay,
  getLiveQuoteUpfront as getQuoteUpfront,
} from './quote/liveQuote.selector';

export * as amend from 'state/secondaryMarket/live/amend/amend.selectors';
export * as form from 'state/secondaryMarket/live/form/liveForm.selectors';
export * as kyc from 'state/secondaryMarket/live/kyc/kyc.selectors';

const selectors = secondaryMarketSelectorsFactory<LiveForm, {}>('live', {});

export function formatToPriceType(state: AppState, tabId: string, price: number | undefined) {
  if (price === undefined) {
    return price;
  }
  const priceType = liveOriginalFormSelectors.getPriceType(state, tabId) ?? getLiveQuotePriceType(state, tabId);
  return priceType === 'Percent' ? price * 100.0 : price;
}

const getFormHasUpFrontFee = (state: AppState, tabId: string): boolean => {
  const tradeWay = selectors.getFormTradeWay(state, tabId);
  if (tradeWay === 'ClientSell') {
    return false;
  }
  return (
    liveFormInputSelectors.getFormInputHasUpfrontFee(state, tabId) ??
    liveOriginalFormSelectors.getFormHasUpfrontFee(state, tabId) ??
    false
  );
};

const getFormCanEditUpfrontFee = (state: AppState, tabId: string): boolean => {
  const tradeWay = selectors.getFormTradeWay(state, tabId);
  if (tradeWay === 'ClientSell') {
    return false;
  }
  return !getTabIsFrozen(state, tabId);
};
const getFormCustomizedXMarginAdjustment = (state: AppState, tabId: string) => {
  return (
    liveFormInputSelectors.getFormInputCustomizedXMarginAdjustment(state, tabId) ??
    liveOriginalFormSelectors.getFormCustomizedXMarginAdjustment(state, tabId)
  );
};

const getFormCustomizedXMarginAdjustmentFormatted = (state: AppState, tabId: string) => {
  const value = getFormCustomizedXMarginAdjustment(state, tabId);
  return formatToPriceType(state, tabId, value);
};

const getFormCustomizedTradingPriceAdjustment = (state: AppState, tabId: string) => {
  return (
    liveFormInputSelectors.getFormInputCustomizedTradingPriceAdjustment(state, tabId) ??
    liveOriginalFormSelectors.getFormCustomizedTradingPriceAdjustment(state, tabId)
  );
};

const getFormCustomizedTradingPriceAdjustmentFormatted = (state: AppState, tabId: string) => {
  const value = getFormCustomizedTradingPriceAdjustment(state, tabId);
  return formatToPriceType(state, tabId, value);
};

const getFormTraceReporting = (state: AppState, tabId: string) => {
  return (
    liveFormInputSelectors.getFormInputTraceReporting(state, tabId) ??
    liveOriginalFormSelectors.getFormTraceReporting(state, tabId)
  );
};
const getKeepAliveProductIds = (state: AppState) => {
  const productIds = Object.entries(state.secondaryMarket.live.form.input)
    .map(([tabId]) => getProductKey(state, tabId))
    .filter(isDefined);
  return new Set(productIds);
};

const getDefaultUpFront = (
  tradeWay: TradeWay | undefined,
  prices: { bid: number | undefined; ask: number | undefined },
) => {
  if (tradeWay === undefined) {
    return undefined;
  }
  return tradeWay === 'ClientSell' ? prices.bid : prices.ask;
};

const getFormDefaultUpFront = (state: AppState, tabId: string) => {
  const tradeWay = selectors.getFormTradeWay(state, tabId);
  const extraBid = liveReferentialSelectors.getExtraBid(state, tabId);
  const extraAsk = liveReferentialSelectors.getExtraAsk(state, tabId);
  return getDefaultUpFront(tradeWay, { ask: extraAsk, bid: extraBid });
};

const getFormUpfront = (state: AppState, tabId: string) => {
  const inputUpfront = liveFormInputSelectors.getFormInputCustomizedUpfront(state, tabId);
  const originalUpfront = liveOriginalFormSelectors.getFormCustomizedUpfront(state, tabId);
  const defaultUpfront = getFormDefaultUpFront(state, tabId);
  const quoteUpfront = getQuoteUpfront(state, tabId);
  return inputUpfront ?? originalUpfront ?? defaultUpfront ?? quoteUpfront;
};

const getFormUpfrontFormatted = (state: AppState, tabId: string) => {
  const value = getFormUpfront(state, tabId);
  return formatToPriceType(state, tabId, value);
};

const getFormPrice = (state: AppState, tabId: string) => {
  const workspace = getWorkspaceType(state, tabId);
  if (workspace === 'secondaryMarketPostEvents') {
    return liveOriginalFormSelectors.getFormSettlementPriceByPriceType(state, tabId);
  }
  const tradeWay = selectors.getFormTradeWay(state, tabId);
  const bid = liveReferentialSelectors.getBid(state, tabId);
  const ask = liveReferentialSelectors.getAsk(state, tabId);

  return (
    liveOriginalFormSelectors.getFormSettlementPriceByPriceType(state, tabId) ??
    getRelevantPriceDependingOnTradeWay(tradeWay, { bid, ask })
  );
};

const getCashAmountPerQuantityUnit = (state: AppState, tabId: string) => {
  const denomination = liveReferentialSelectors.getDenomination(state, tabId);
  const priceType = liveReferentialSelectors.getPriceType(state, tabId)!;
  const price = getFormPrice(state, tabId);
  return computeCashAmount(price, denomination, priceType);
};

const getAmendRequest = (state: AppState, tabId: string): Partial<LiveTradeAmendRequest> => {
  const orderId = fromSecondaryPostEventsWorkspace.getOrderId(state, tabId);

  const client = liveFormInputSelectors.getFormClient(state, tabId);
  const clientType = liveFormInputSelectors.getFormClientType(state, tabId);
  const salesId = selectors.getFormSalesId(state, tabId);
  const custodianBdrId = liveFormInputSelectors.getFormCustodianBdrId(state, tabId);
  const settlementDate = liveFormInputSelectors.getFormSettlementDate(state, tabId);
  const priceType = liveOriginalFormSelectors.getPriceType(state, tabId);

  const salesMarginAdjusted = liveFormInputSelectors.getFormInputCustomizedXMarginAdjustmentFormatted(state, tabId);
  const tradingPriceAdjusted = liveFormInputSelectors.getFormInputCustomizedTradingPriceAdjustmentFormatted(
    state,
    tabId,
  );

  const settlementType = selectors.getFormSettlement(state, tabId);
  const comment = liveFormInputSelectors.getFormComment(state, tabId);
  const performTraceReporting = liveFormInputSelectors.getFormInputTraceReporting(state, tabId);
  const upfrontAdjusted = liveFormInputSelectors.getFormInputCustomizedUpfrontFormatted(state, tabId);
  const quantity = liveFormInputSelectors.getFormQuantity(state, tabId);
  const cashAmount = liveFormInputSelectors.getFormQuantity(state, tabId);
  const tradeSizeMode = liveFormInputSelectors.getFormTradeSizeInputMode(state, tabId);
  const modifiedFields = liveFormInputSelectors.getFormChanges(state, tabId);

  return {
    clientType: clientType,
    clientEliotCode: client?.eliotCode,
    custodianBdrId: custodianBdrId,
    salesId: salesId,
    orderId: orderId,
    settlementDate: settlementDate,
    settlementType: modifiedFields.includes('settlementType') ? settlementType : undefined,
    comment: comment,
    performTraceReporting: performTraceReporting,
    salesMarginAdjustment: salesMarginAdjusted,
    tradingPriceAdjustment: tradingPriceAdjusted,
    upfrontAdjustment: upfrontAdjusted,
    priceType,
    quantity: tradeSizeMode === 'quantity' ? quantity : undefined,
    cashAmount: tradeSizeMode === 'amount' ? cashAmount : undefined,
  } satisfies LiveTradeAmendRequest;
};

const getStatus = createSelector(
  [
    (appState: AppState, tabId: string) =>
      selectors
        .makeGetValidationErrors()(appState, tabId)
        .some(e => e.isMandatory),
    getLiveQuoteStatus,
    liveTradeSelectors.getTradeStatus,
  ],
  (hasErrorsOnMandatoryFields, quoteStatus, tradeStatus) => {
    if (tradeStatus !== undefined) {
      return tradeStatus as LiveStatus;
    }
    if (quoteStatus !== undefined) {
      return quoteStatus as LiveStatus;
    }
    return hasErrorsOnMandatoryFields ? ('NOT_READY' as LiveStatus) : ('READY' as LiveStatus);
  },
);
const getQuotationType = (appState: AppState, tabId: string): SecondaryQuotationType | undefined => {
  return getLiveQuoteQuotationType(appState, tabId) ?? liveReferentialSelectors.getQuotationType(appState, tabId);
};
const getStreamingPriceForDisplay = (appState: AppState, tabId: string): StreamingPriceForDisplay | undefined => {
  const tradeWay = getLiveQuoteTradeWay(appState, tabId);
  if (!tradeWay) {
    return;
  }
  return getStreamingPriceForDisplayByTradeway(appState, getProductKey(appState, tabId), tradeWay);
};
const getStreamingStatus = (appState: AppState, tabId: string): boolean => {
  return getRealtimeStatus(appState, getProductKey(appState, tabId));
};
const getStreamingAskSettlementPrice = (appState: AppState, tabId: string): TrackedValue | undefined => {
  return getRealtimeAskSettlementPrice(appState, getProductKey(appState, tabId));
};
const getStreamingBidSettlementPrice = (appState: AppState, tabId: string): TrackedValue | undefined => {
  return getRealtimeBidSettlementPrice(appState, getProductKey(appState, tabId));
};
const getStreamingUnderlyingPrice = (appState: AppState, tabId: string) => {
  return getRealtimeUnderlyingPrice(appState, getProductKey(appState, tabId));
};
const getStreamingUnderlyingPriceUnit = (appState: AppState, tabId: string) => {
  return getRealtimeUnderlyingPriceUnit(appState, getProductKey(appState, tabId));
};
function getStreamingLastUpdate(appState: AppState, tabId: string) {
  return getRealtimeLastUpdate(appState, getProductKey(appState, tabId));
}

const getSalesAdjustedPrice = (appState: AppState, tabId: string) => {
  if (liveTradeSelectors.getTradeStatus(appState, tabId) === 'TRADE_DONE') {
    return liveTradeSelectors.getSettlementPrice(appState, tabId);
  }
  const price = getLiveQuotePrice(appState, tabId);
  return computeSalesAdjustedPrice(appState, tabId, price);
};
const getSalesAdjustedSettlementPriceFormatted = (state: AppState, tabId: string) => {
  const price = getSalesAdjustedPrice(state, tabId);
  return formatToPriceType(state, tabId, price);
};

const getEffectiveCashAmount = (state: AppState, tabId: string) => {
  let price = getSalesAdjustedPrice(state, tabId);
  const denomination = liveReferentialSelectors.getDenomination(state, tabId);
  const quantity = liveTradeSelectors.getTradeQuantity(state, tabId) ?? getLiveQuoteQuantity(state, tabId);
  const priceType = getLiveQuotePriceType(state, tabId);

  if (price === undefined || denomination === undefined || quantity === undefined) {
    return undefined;
  }
  price = price * quantity;
  if (priceType === 'Percent') {
    return price * denomination;
  }
  return price;
};

const getEffectivePriceFormatted = (state: AppState, tabId: string) => {
  const price = liveTradeSelectors.getSettlementPrice(state, tabId) ?? getLiveQuotePrice(state, tabId);
  return formatToPriceType(state, tabId, price);
};

const getErrorMessage = (state: AppState, tabId: string) =>
  liveTradeSelectors.getTradeErrorMessage(state, tabId) ?? getLiveQuoteErrorMessage(state, tabId);

const getTimeout = (state: AppState, tabId: string) =>
  liveTradeSelectors.getTradeTimeout(state, tabId) ?? getLiveQuoteTimeout(state, tabId);

export const liveSelectors = {
  ...selectors,
  getFormTraceReporting,
  getFormCustomizedTradingPriceAdjustmentFormatted,
  getFormCustomizedTradingPriceAdjustment,
  getFormCustomizedXMarginAdjustmentFormatted,
  getFormCustomizedXMarginAdjustment,
  getFormCanEditUpfrontFee,
  getFormHasUpFrontFee,
  getKeepAliveProductIds,
  getStreamingPriceForDisplay,
  getStreamingStatus,
  getStreamingAskSettlementPrice,
  getStreamingBidSettlementPrice,
  getStreamingUnderlyingPrice,
  getStreamingUnderlyingPriceUnit,
  getStreamingLastUpdate,
  getQuotationType,
  getStatus,
  getAmendRequest,
  getFormPrice,
  getDefaultUpFront,
  getFormDefaultUpFront,
  getFormUpfront,
  getFormUpfrontFormatted,
  getSalesAdjustedPrice,
  getSalesAdjustedSettlementPriceFormatted,
  getEffectiveCashAmount,
  getEffectivePriceFormatted,
  getCashAmountPerQuantityUnit,
  getErrorMessage,
  getTimeout,
};

export type LiveSelectors = typeof liveSelectors;
